import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { auth } from "@/api/auth";
import { ApiError } from "@/api/http";
import { useAuth } from "@/auth/AuthContext";
import { useBranding } from "@/branding/BrandingLoader";
import { Button, Card, CardBody, CardHeader, FormError, Input, Label } from "@/components/ui";

const DEFAULT_ORG = "00000000-0000-0000-0000-000000000001";

export function RegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgId, setOrgId] = useState(DEFAULT_ORG);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.register({ email, password, org_id: orgId });
      // Auto-login after successful registration.
      await login(email, password);
      navigate("/portfolio", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message.replace(/^\d+:\s*/, "") : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <h1 className="text-center text-lg font-semibold">
            Create your {branding?.name ?? "Piggy"} account
          </h1>
        </CardHeader>
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
              <p className="mt-1 text-xs text-slate-500">At least 8 characters.</p>
            </div>
            <div>
              <Label htmlFor="org">Organization ID</Label>
              <Input id="org" value={orgId} onChange={(e) => setOrgId(e.target.value)} />
              <p className="mt-1 text-xs text-slate-500">
                Leave the default unless your admin gave you a specific org id.
              </p>
            </div>
            <FormError>{error}</FormError>
            <Button type="submit" loading={submitting} className="w-full">
              Sign up
            </Button>
          </form>
          <div className="mt-4 text-center text-xs text-slate-500">
            <Link to="/login" className="hover:text-brand">
              Already have an account? Sign in
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
